import React, { useState } from "react";
import { Pencil, Check, X } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

/**
 * Inline bio editor. Click pencil → textarea opens. Save / cancel.
 * Renders read-only `bio` text if not editing.
 */
export default function BioEditor({ value, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(value || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (busy) return;
    const trimmed = text.trim().slice(0, 500);
    setBusy(true);
    try {
      await api.put("/me/profile", { bio: trimmed });
      toast.success("Bio saved");
      onSaved?.(trimmed || null);
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    } finally {
      setBusy(false);
    }
  };

  if (!editing) {
    return (
      <div className="flex items-start gap-2 mt-3 border border-white/10 bg-[#0a0a0a] px-4 py-3" data-testid="profile-bio-display">
        <div className="flex-1 min-w-0">
          {value ? (
            <p className="text-zinc-300 leading-relaxed text-sm sm:text-base whitespace-pre-wrap">
              {value}
            </p>
          ) : (
            <p className="text-zinc-600 italic text-sm">
              Add a bio so other Vault users know your beat.
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={() => setEditing(true)}
          data-testid="profile-bio-edit-btn"
          className="shrink-0 text-zinc-500 hover:text-[#a78bfa] transition-colors p-1"
          title="Edit bio"
        >
          <Pencil size={14} />
        </button>
      </div>
    );
  }
  return (
    <div className="mt-3" data-testid="profile-bio-editor">
      <textarea
        autoFocus
        value={text}
        onChange={(e) => setText(e.target.value.slice(0, 500))}
        rows={3}
        placeholder="A line or two about what you investigate, your sources, your beat…"
        className="w-full bg-[#050505] border border-[#a78bfa]/50 focus:border-[#a78bfa] outline-none px-3 py-2 text-sm text-white placeholder:text-zinc-600 resize-none"
      />
      <div className="flex items-center justify-between mt-2">
        <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-500">
          {text.length}/500
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => { setEditing(false); setText(value || ""); }}
            className="px-3 py-1 border border-white/10 text-zinc-400 hover:text-white font-mono text-[10px] tracking-[0.2em] uppercase"
          >
            <X size={11} className="inline mr-1" /> Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={busy}
            data-testid="profile-bio-save-btn"
            className="px-3 py-1 bg-[#a78bfa] text-[#050505] hover:bg-white font-mono text-[10px] tracking-[0.2em] uppercase disabled:opacity-60"
          >
            <Check size={11} className="inline mr-1" /> Save
          </button>
        </div>
      </div>
    </div>
  );
}
